import React, { useCallback } from 'react'
import styled from '@emotion/styled'
import { Radio, DatePicker } from 'antd'
import { createSimpleStore } from 'react-simple-reducer'
import dayjs from 'dayjs'
import Axios from 'axios'
import { MensalChart } from './MensalChart'

export type Metric = 'count' | 'valorNewDriverSum'
export type Mode = 'workdays' | 'all_days'

export const MAIN_COLOR = '#1677ff'
export const COMPARING_COLOR = '#93c5fd'

export interface WorkdayPoint {
  workdayIndex: number
  date: string
  count: number
  valorNewDriverSum: number
  isWorkday: boolean
}

type Tipo = 'todos' | 'venda_direta' | 'parceiros'

export const BiMensalStore = createSimpleStore(
  {
    tipo: 'todos' as Tipo,
    metric: 'valorNewDriverSum' as Metric,
    mode: 'workdays' as Mode,
    month: currentMonth(),
    comparingMonth: previousMonth(),
    loading: false as boolean,
    monthPoints: [] as WorkdayPoint[],
    comparingMonthPoints: [] as WorkdayPoint[],
    error: null as string | null,
  },
  {
    setTipo: (state, tipo: Tipo) => ({ ...state, tipo }),
    setMetric: (state, metric: Metric) => ({ ...state, metric }),
    setMode: (state, mode: Mode) => ({ ...state, mode }),
    setMonth: (state, month: string) => ({ ...state, month }),
    setComparingMonth: (state, comparingMonth: string) => ({ ...state, comparingMonth }),
    fetchStarted: (state) => ({ ...state, loading: true, error: null }),
    fetchSuccess: (
      state,
      payload: { monthPoints: WorkdayPoint[]; comparingMonthPoints: WorkdayPoint[] }
    ) => ({ ...state, ...payload, loading: false }),
    fetchFailed: (state, error: string) => ({ ...state, loading: false, error }),
  },
  {
    thunks: {
      fetchData(tipo: Tipo, month: string, comparingMonth: string, mode: Mode) {
        return async (dispatch: any) => {
          dispatch(BiMensalStore.actions.fetchStarted())
          try {
            const { data } = await Axios.get('/matriculas/bi/mensal', {
              params: { tipo, month, comparingMonth, mode },
            })
            dispatch(
              BiMensalStore.actions.fetchSuccess({
                monthPoints: data.month,
                comparingMonthPoints: data.comparingMonth,
              })
            )
          } catch {
            dispatch(BiMensalStore.actions.fetchFailed('Erro ao carregar dados'))
          }
        }
      },
    },
  }
)

export function BiEvolucaoMensal() {
  const init = useCallback((dispatch: any) => {
    dispatch(BiMensalStore.thunks.fetchData('todos', currentMonth(), previousMonth(), 'workdays'))
  }, [])

  return (
    <BiMensalStore.Provider init={init}>
      <Controls>
        <TipoToggle />
        <MonthPickers />
        <MetricToggle />
        <ModeToggle />
      </Controls>
      <MensalChart />
    </BiMensalStore.Provider>
  )
}

function TipoToggle() {
  const { tipo, month, comparingMonth, mode } = BiMensalStore.useState()
  const dispatch = BiMensalStore.useDispatch()

  function handleChange(newTipo: Tipo) {
    dispatch(BiMensalStore.actions.setTipo(newTipo))
    dispatch(BiMensalStore.thunks.fetchData(newTipo, month, comparingMonth, mode))
  }

  return (
    <Radio.Group
      value={tipo}
      onChange={(e) => handleChange(e.target.value)}
      optionType="button"
      buttonStyle="solid"
      options={[
        { label: 'Todos', value: 'todos' },
        { label: 'Venda Direta', value: 'venda_direta' },
        { label: 'Parceiros', value: 'parceiros' },
      ]}
    />
  )
}

function MonthPickers() {
  const { month, comparingMonth, tipo, mode } = BiMensalStore.useState()
  const dispatch = BiMensalStore.useDispatch()
  const maxMonth = dayjs()

  function handleMonthChange(newMonth: string) {
    dispatch(BiMensalStore.actions.setMonth(newMonth))
    dispatch(BiMensalStore.thunks.fetchData(tipo, newMonth, comparingMonth, mode))
  }

  function handleComparingMonthChange(newMonth: string) {
    dispatch(BiMensalStore.actions.setComparingMonth(newMonth))
    dispatch(BiMensalStore.thunks.fetchData(tipo, month, newMonth, mode))
  }

  return (
    <MonthPickerRow>
      <MonthLabel color={MAIN_COLOR}>Mês</MonthLabel>
      <DatePicker
        picker="month"
        value={dayjs(month)}
        onChange={(d) => d && handleMonthChange(d.format('YYYY-MM'))}
        disabledDate={(d) => d.isAfter(maxMonth, 'month')}
        format="MM/YYYY"
        allowClear={false}
      />
      <MonthLabel color={COMPARING_COLOR}>Comparar com</MonthLabel>
      <DatePicker
        picker="month"
        value={dayjs(comparingMonth)}
        onChange={(d) => d && handleComparingMonthChange(d.format('YYYY-MM'))}
        disabledDate={(d) => d.isAfter(maxMonth, 'month')}
        format="MM/YYYY"
        allowClear={false}
      />
    </MonthPickerRow>
  )
}

function MetricToggle() {
  const { metric } = BiMensalStore.useState()
  const dispatch = BiMensalStore.useDispatch()

  return (
    <Radio.Group
      value={metric}
      onChange={(e) => dispatch(BiMensalStore.actions.setMetric(e.target.value))}
      optionType="button"
      options={[
        { label: 'Receita', value: 'valorNewDriverSum' },
        { label: 'Matrículas', value: 'count' },
      ]}
    />
  )
}

function ModeToggle() {
  const { mode, tipo, month, comparingMonth } = BiMensalStore.useState()
  const dispatch = BiMensalStore.useDispatch()

  function handleChange(newMode: Mode) {
    dispatch(BiMensalStore.actions.setMode(newMode))
    dispatch(BiMensalStore.thunks.fetchData(tipo, month, comparingMonth, newMode))
  }

  return (
    <Radio.Group
      value={mode}
      onChange={(e) => handleChange(e.target.value)}
      optionType="button"
      options={[
        { label: 'Dias Úteis', value: 'workdays' },
        { label: 'Todos os Dias', value: 'all_days' },
      ]}
    />
  )
}

const Controls = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 8px;
`

const MonthPickerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const MonthLabel = styled.span<{ color: string }>`
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => p.color};
`

function currentMonth(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

function previousMonth(): string {
  const now = new Date()
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  return `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`
}
